/**
 * Simple console-only logger for development debugging
 * No database persistence, only console output
 */

import { getBrandLogMetadataFromRequest } from '@lib-server/brand/loggingContext';
import { getRequestContext } from '@lib-server/requestContext';
import type { IncomingMessage } from 'http';
import type { NextApiRequest } from 'next';
import type { Session } from 'next-auth';
import util from 'util';

type RequestLike = Pick<IncomingMessage, 'headers'> &
  Partial<Pick<NextApiRequest, 'method' | 'url' | 'query' | 'socket'>>;

interface SimpleLogContext {
  feature?: string;
  action?: string;
  resource?: string;
  metadata?: Record<string, unknown>;
  userId?: string;
  userEmail?: string | null;
  method?: string;
  url?: string;
}

class SimpleLogger {
  private shouldLog(level: string): boolean {
    const logLevel = (process.env.LOG_LEVEL || 'info').toLowerCase();
    const levelPriority: Record<string, number> = {
      error: 0,
      warn: 1,
      info: 2,
      debug: 3,
    };

    const requestedPriority = levelPriority[level.toLowerCase()] ?? 2;
    const threshold = levelPriority[logLevel] ?? 2;

    // Always log errors and warnings regardless of environment to aid production debugging.
    if (requestedPriority <= levelPriority.error) return true;
    if (requestedPriority <= levelPriority.warn) return true;

    return requestedPriority <= threshold;
  }

  private log(
    level: string,
    message: string,
    context?: SimpleLogContext,
    error?: Error
  ): void {
    if (!this.shouldLog(level)) return;

    const timestamp = new Date().toISOString();
    const logMessage = `[${timestamp}] ${level.toUpperCase()}: ${message}`;

    const metadata = {
      ...context,
      ...(error && {
        error: {
          name: error.name,
          message: error.message,
          stack: error.stack,
        },
      }),
    };

    // Force deep, readable serialization to avoid "[Object]" in some collectors
    const serialized = this.serialize(metadata);

    // Pretty-print on a new line for readability unless explicitly disabled
    const pretty = process.env.LOG_PRETTY !== 'false';
    const line = pretty
      ? `${logMessage}\n${serialized}`
      : `${logMessage} ${serialized}`;
    switch (level.toLowerCase()) {
      case 'error':
        // eslint-disable-next-line no-console
        console.error(line);
        break;
      case 'warn':
        // eslint-disable-next-line no-console
        console.warn(line);
        break;
      case 'info':
        // eslint-disable-next-line no-console
        console.info(line);
        break;
      default:
        // eslint-disable-next-line no-console
        console.log(line);
    }
  }

  private serialize(obj: unknown): string {
    try {
      // Prefer util.inspect for deep inspection without throwing on circular refs
      return util.inspect(obj, {
        depth: 8,
        colors: false,
        maxArrayLength: 200,
        breakLength: 100,
        compact: false, // multi-line, human-readable
      });
    } catch {
      try {
        return JSON.stringify(obj, null, 2);
      } catch {
        return String(obj);
      }
    }
  }

  private extractRequestMetadata(req?: RequestLike) {
    const requestId =
      (req?.headers['x-request-id'] as string) ||
      (req?.headers['x-correlation-id'] as string);

    return {
      ...(requestId ? { requestId } : {}),
      ...(req?.headers['user-agent']
        ? { userAgent: req.headers['user-agent'] }
        : {}),
      ...(req?.headers['x-forwarded-for'] || req?.socket?.remoteAddress
        ? { ip: req?.headers['x-forwarded-for'] || req?.socket?.remoteAddress }
        : {}),
      ...getBrandLogMetadataFromRequest(req),
    };
  }

  private extractActiveRequestMetadata() {
    const requestContext = getRequestContext();
    if (!requestContext) return {};

    return {
      requestId: requestContext.requestId,
      ...(requestContext.userAgent
        ? { userAgent: requestContext.userAgent }
        : {}),
      ...(requestContext.ip ? { ip: requestContext.ip } : {}),
      brandKey: requestContext.brandKey,
      brandEnvironment: requestContext.brandEnvironment,
      requestHost: requestContext.requestHost,
      requestOrigin: requestContext.requestOrigin,
      canonicalOrigin: requestContext.canonicalOrigin,
      isApprovedHost: requestContext.isApprovedHost,
      ...(requestContext.partnerKey
        ? { partnerKey: requestContext.partnerKey }
        : {}),
      ...(requestContext.partnerId
        ? { partnerId: requestContext.partnerId }
        : {}),
    };
  }

  private extractContext(
    context?: Partial<SimpleLogContext>,
    session?: Session | null,
    req?: RequestLike
  ): SimpleLogContext {
    const requestContext = getRequestContext();

    const metadata = {
      ...this.extractActiveRequestMetadata(),
      ...(req ? this.extractRequestMetadata(req) : {}),
      ...(context?.metadata || {}),
      ...(req?.query && Object.keys(req.query).length > 0
        ? { query: req.query }
        : {}),
    };

    return {
      ...context,
      ...(requestContext?.userId && !session
        ? {
            userId: requestContext.userId,
          }
        : {}),
      ...(session ? this.extractUser(session) : {}),
      method: req?.method ?? context?.method ?? requestContext?.method,
      url: req?.url ?? context?.url ?? requestContext?.url,
      ...(Object.keys(metadata).length > 0 ? { metadata } : {}),
    };
  }

  private extractUser(session?: Session | null) {
    if (!session?.user) {
      return {};
    }

    const user = session.user as Session['user'] & {
      id?: string | null;
    };

    const result: Partial<SimpleLogContext> = {};

    if (typeof user.id === 'string' && user.id.length > 0) {
      result.userId = user.id;
    }

    if (typeof user.email === 'string') {
      result.userEmail = user.email;
    }

    return result;
  }

  error(
    message: string,
    context?: Partial<SimpleLogContext>,
    error?: Error,
    session?: Session | null,
    req?: RequestLike
  ): void {
    const fullContext = this.extractContext(context, session, req);
    this.log('error', message, fullContext, error);
  }

  warn(
    message: string,
    context?: Partial<SimpleLogContext>,
    session?: Session | null,
    req?: RequestLike
  ): void {
    const fullContext = this.extractContext(context, session, req);
    this.log('warn', message, fullContext);
  }

  info(
    message: string,
    context?: Partial<SimpleLogContext>,
    session?: Session | null,
    req?: RequestLike
  ): void {
    const fullContext = this.extractContext(context, session, req);
    this.log('info', message, fullContext);
  }

  debug(
    message: string,
    context?: Partial<SimpleLogContext>,
    session?: Session | null,
    req?: RequestLike
  ): void {
    const fullContext = this.extractContext(context, session, req);
    this.log('debug', message, fullContext);
  }

  // Legacy method compatibility
  apiLog(req: NextApiRequest, res: any, duration: number): void {
    if (this.shouldLog('info')) {
      // eslint-disable-next-line no-console
      console.log(
        `API: ${req.method} ${req.url} - ${res.statusCode} (${duration}ms)`
      );
    }
  }

  // Legacy method compatibility
  userAction(
    message: string,
    context?: any,
    session?: Session | null,
    req?: RequestLike
  ): void {
    this.info(`User Action: ${message}`, context, session, req);
  }
}

// Export singleton instance
export const logger = new SimpleLogger();
export default logger;
